import { useRef, type MouseEvent, type ReactNode } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Collapse, Button, Space } from "antd";
import type { CollapseProps } from "antd";

import Note from "@components/Note";
import { hasAccess } from "@components/Authorization";
import { W1, W3 } from "@components/Constants";
import { updateInvestmentNoteOperation } from "@api/services/investments";

import Footer from "./Footer";
import InvestmentSteps from "./InvestmentSteps";
import InvestmentFunding from "./InvestmentFunding";
import InvestmentDescriptions from "./InvestmentDescriptions";
import InvestmentProfileUpdateDrawer from "./InvestmentProfileUpdateDrawer";
import InvestmentAccreditationUpdateModal from "./InvestmentAccreditationUpdateModal";
import type {
  Account,
  ModalHandle,
  DrawerHandle,
  Investment as InvestmentItem,
  TemplatesStore,
  ReadInvestment,
  UpdateInvestment,
} from "../types";

const LABEL_NOTE = "Note";
const LABEL_EDIT = "Edit";
const LABEL_FUNDING = "Funding";
const LABEL_INVESTMENT = "Investment";

const KEY_NOTE = "note";
const KEY_FUNDING = "funding";
const KEY_DESCRIPTIONS = "descriptions";

const LABEL_STYLE = { fontWeight: 600 };
const COLLAPSE_STYLE = { background: "transparent" };

type Props = {
  account: Account;
  investment: InvestmentItem;
  readInvestment: ReadInvestment;
  updateInvestment: UpdateInvestment;
  projectTemplatesStore: TemplatesStore;
};

const Investment = ({
  account,
  investment,
  readInvestment,
  updateInvestment,
  projectTemplatesStore,
}: Props) => {
  const investmentProfileUpdateDrawer = useRef<DrawerHandle>({});
  const investmentAccreditationUpdateModal = useRef<ModalHandle>({});

  const isNoteEditable = hasAccess([ "investments-note-write" ]);
  const canUpdateInvestment = hasAccess([ "investments-write" ]);

  const {
    isInvestmentFundedTemplateEnabled,
    isInvestmentSignedTemplateEnabled,
  } = projectTemplatesStore;

  const renderLabel = (value: ReactNode) =>
    <span style={LABEL_STYLE}>{value}</span>;

  const openInvestmentProfileUpdateDrawer = () =>
    investmentProfileUpdateDrawer.current.open();

  const openInvestmentAccreditationUpdateModal = () => {
    const item = {
      id: investment.id,
      hasAccreditation: investment.investor?.hasAccreditation,
      accreditationExpiresAt: investment.investor?.accreditationExpiresAt,
    };

    investmentAccreditationUpdateModal.current.openItem(item);
  };

  const onClick = (e: MouseEvent) => {
    e.stopPropagation();
    openInvestmentProfileUpdateDrawer();
  };

  // NOTE: the parameter is optional because TypeScript infers the callback
  //       prop of the JavaScript Note component from its empty default.
  const onAfterNoteSubmit = (investmentNoteAttributes: Partial<InvestmentItem> = {}) =>
    updateInvestment({ ...investment, ...investmentNoteAttributes });

  const getExtra = () => (!canUpdateInvestment
    ? null
    : (
      <Button
        key="edit"
        icon={<EditOutlined />}
        size="small"
        shape="round"
        onClick={onClick}
      >
        {LABEL_EDIT}
      </Button>
    ));

  const items: CollapseProps["items"] = [
    {
      key: KEY_DESCRIPTIONS,
      label: renderLabel(LABEL_INVESTMENT),
      extra: getExtra(),
      children: (
        <InvestmentDescriptions
          account={account}
          investment={investment}
          readInvestment={readInvestment}
          openInvestmentProfileUpdateDrawer={openInvestmentProfileUpdateDrawer}
          openInvestmentAccreditationUpdateModal={openInvestmentAccreditationUpdateModal}
        />
      ),
      collapsible: "icon",
    },
    {
      key: KEY_FUNDING,
      label: renderLabel(LABEL_FUNDING),
      children: (
        <InvestmentFunding
          investment={investment}
          readInvestment={readInvestment}
          isInvestmentFundedTemplateEnabled={isInvestmentFundedTemplateEnabled}
        />
      ),
      collapsible: "icon",
    },
    {
      key: KEY_NOTE,
      label: renderLabel(LABEL_NOTE),
      children: (
        <Note
          id={investment.id}
          note={investment.note}
          updatedAt={investment.noteUpdatedAt}
          updatedBy={investment.noteUpdatedBy}
          operation={updateInvestmentNoteOperation}
          isEditable={isNoteEditable}
          onAfterSubmit={onAfterNoteSubmit}
        />
      ),
      collapsible: "icon",
    },
  ];

  const style = {
    marginBottom: W3,
  };

  const defaultActiveKey = [
    KEY_DESCRIPTIONS,
  ];

  const collapseProps: CollapseProps = {
    size: "small",
    style: COLLAPSE_STYLE,
    bordered: false,
    items,
    defaultActiveKey,
  };

  return (
    <Space
      size={W1}
      style={style}
      direction="vertical"
    >
      <InvestmentSteps
        investment={investment}
        readInvestment={readInvestment}
        isInvestmentSignedTemplateEnabled={isInvestmentSignedTemplateEnabled}
      />

      <div className="descriptions">
        <Collapse {...collapseProps} />
      </div>

      <Footer
        investment={investment}
        updateInvestment={updateInvestment}
      />

      <InvestmentProfileUpdateDrawer
        drawer={investmentProfileUpdateDrawer}
        account={account}
        investment={investment}
        updateInvestment={updateInvestment}
      />

      <InvestmentAccreditationUpdateModal
        modal={investmentAccreditationUpdateModal}
        onSuccess={updateInvestment}
      />
    </Space>
  );
};

export default Investment;
